import React, { useState, useEffect } from 'react';
import './Announcement.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import BatchViewer from '../Components/Viewers/BatchViewer';
import { getDistinctFilters } from '../Components/Menu/Branch';
import { batchDetailsAPI, facultyAPI, funcAreaAPI, trngCourseAPI, trainingNeedsAPI, smsAPI, batchStatusAPI, emailAPI } from './api';
import { useAuth } from './auth';

function Announcement() {
  const { user } = useAuth();
  const [batchSrNo, setBatchSrNo] = useState('');
  const [showBatchViewer, setShowBatchViewer] = useState(false);
  const [zone, setZone] = useState('');
  const [branch, setBranch] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [batchInfo, setBatchInfo] = useState(null);
  const [facultyName, setFacultyName] = useState('');
  const [funcSubAreaName, setFuncSubAreaName] = useState('');
  const [trngCourseName, setTrngCourseName] = useState('');
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [batchError, setBatchError] = useState('');
  const [announceMsg, setAnnounceMsg] = useState(null);
  const [isFreelanceBatch, setIsFreelanceBatch] = useState(false);
  const [announceSummary, setAnnounceSummary] = useState(null);
  const [branches, setBranches] = useState([]);
  const [zones, setZones] = useState([]);
  const [fromDateOpen, setFromDateOpen] = useState(false);
  const [toDateOpen, setToDateOpen] = useState(false);

  useEffect(() => {
    getDistinctFilters().then(({ branches: b, zones: z }) => {
      setBranches(b);
      setZones(z);
    });
  }, []);

  const handleCheckAll = (e) => {
    const isChecked = e.target.checked;
    setCheckAll(isChecked);
    const newChecked = {};
    participants.forEach((_, i) => { newChecked[i] = isChecked; });
    setCheckedItems(newChecked);
  };

  const handleItemCheck = (index) => {
    const newChecked = { ...checkedItems, [index]: !checkedItems[index] };
    setCheckedItems(newChecked);
    setCheckAll(participants.every((_, i) => newChecked[i]));
  };

  const handleBatchSelect = (batch) => {
    setBatchSrNo(batch.batch_sr_no || '');
    setShowBatchViewer(false);
  };

  const handleShowShortListed = async (e) => {
    e.preventDefault();

    const srNo = String(batchSrNo).trim();
    if (!srNo) {
      alert('Please enter a Batch Sr.No.');
      return;
    }

    setLoading(true);
    setBatchError('');
    setAnnounceMsg(null);
    setAnnounceSummary(null);
    setShowResults(false);

    try {
      const batchData = await batchDetailsAPI.getBatch(srNo);

      if (!batchData) {
        alert(`No batch found for Batch Sr.No.: ${srNo}`);
        setLoading(false);
        return;
      }

      const status = (batchData.trng_status || '').toUpperCase();

      const isFreelance = (batchData.batch_type || '').toUpperCase() === 'FREE';
      setIsFreelanceBatch(isFreelance);

      setBatchInfo({
        batch_sr_no: batchData.batch_sr_no || srNo,
        division: 'Appliances',
        zone: zone,
        branch: batchData.branch || '',
        from_date: fromDate,
        to_date: toDate,
        func_sub_area_code: batchData.func_sub_area_code || '',
        trng_course_code: batchData.trng_course_code || '',
        start_date: batchData.start_date || '',
        end_date: batchData.end_date || '',
        start_time: batchData.start_time || '',
        end_time: batchData.end_time || '',
        venue: batchData.venue || '',
        batch_size: batchData.batch_size || '',
        trng_status: batchData.trng_status || '',
        total_faculty_cost: batchData.total_faculty_cost || '',
        faculty_code: batchData.faculty_code || '',
        faculty_code_1: batchData.faculty_code_1 || '',
        faculty_code_2: batchData.faculty_code_2 || '',
        faculty_code_3: batchData.faculty_code_3 || '',
        faculty_code_4: batchData.faculty_code_4 || '',
        batch_type: batchData.batch_type || ''
      });

      // Fetch faculty names for all faculty codes
      const allFacultyCodes = [
        batchData.faculty_code, batchData.faculty_code_1, batchData.faculty_code_2,
        batchData.faculty_code_3, batchData.faculty_code_4,
      ].filter(Boolean);
      if (allFacultyCodes.length) {
        try {
          const names = await Promise.all(
            allFacultyCodes.map(fc =>
              facultyAPI.getAll({ faculty_code: fc })
                .then(fd => (Array.isArray(fd) && fd[0]?.faculty_name) ? fd[0].faculty_name : String(fc))
                .catch(() => String(fc))
            )
          );
          setFacultyName(names.join(', '));
        } catch { setFacultyName(allFacultyCodes.join(', ')); }
      } else { setFacultyName(''); }

      // Fetch functional area name
      if (batchData?.func_sub_area_code) {
        try {
          const funcAreaData = await funcAreaAPI.getAll({ func_sub_area_code: batchData.func_sub_area_code });
          const funcArea = Array.isArray(funcAreaData) ? funcAreaData[0] : null;
          setFuncSubAreaName(funcArea?.func_sub_area_name ?? batchData.func_sub_area_code);
        } catch { setFuncSubAreaName(batchData.func_sub_area_code); }
      } else { setFuncSubAreaName(''); }

      // Fetch training course name
      if (batchData?.trng_course_code) {
        try {
          const trngCourseData = await trngCourseAPI.getAll({ trng_course_code: batchData.trng_course_code });
          const trngCourse = Array.isArray(trngCourseData) ? trngCourseData[0] : null;
          setTrngCourseName(trngCourse?.trng_course_name ?? batchData.trng_course_code);
        } catch { setTrngCourseName(batchData.trng_course_code); }
      } else { setTrngCourseName(''); }

      if (status === 'COMPLETED') {
        setBatchError('You have already Marked this batch as Completed.');
        setShowResults(true);
        setLoading(false);
        return;
      }
      if (status === 'CANCELLED') {
        setBatchError('You have already Marked this batch as Cancelled.');
        setShowResults(true);
        setLoading(false);
        return;
      }

      const toLocalDateStr = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const participantsParams = {
        ...(zone && { zone }),
        ...(branch && { branch }),
        ...(fromDate && { from_date: toLocalDateStr(fromDate) }),
        ...(toDate && { to_date: toLocalDateStr(toDate) }),
        ...(batchData?.trng_course_code && { trainingcoursecode: batchData.trng_course_code }),
        nomin_confirm:'N',
      };

      if (isFreelance) {
        await batchStatusAPI.update(srNo, { trng_status: 'SCHEDULED', branch: batchData.branch || '' });

        // Send faculty emails for Freelance batch
        const freelanceTrainingData = {
          batchSrNo: srNo,
          funcSubAreaName: '',
          trngCourseName: '',
          startDate: batchData.start_date ? new Date(batchData.start_date).toLocaleDateString('en-GB') : '',
          endDate: batchData.end_date ? new Date(batchData.end_date).toLocaleDateString('en-GB') : '',
          startTime: batchData.start_time || '',
          endTime: batchData.end_time || '',
          venue: batchData.venue || '',
          trngLocation: batchData.trng_location || '',
          trngCity: batchData.trng_city || '',
          batchSize: batchData.batch_size || '',
          faculty: ''
        };
        const freelanceFacultyCodes = [
          batchData.faculty_code,
          batchData.faculty_code_1,
          batchData.faculty_code_2,
          batchData.faculty_code_3,
          batchData.faculty_code_4,
        ].filter(Boolean);
        for (const fc of freelanceFacultyCodes) {
          try {
            const fd = await facultyAPI.getAll({ faculty_code: fc });
            const fac = Array.isArray(fd) ? fd[0] : null;
            if ((fac?.faculty_email || fac?.email) && (fac.faculty_email || fac.email).includes('@')) {
              emailAPI.notifyFaculty({
                facultyData: { emailId: fac.faculty_email || fac.email, name: fac.faculty_name || '' },
                trainingData: freelanceTrainingData
              }).catch(() => {});
            }
          } catch { /* skip */ }
        }

        setParticipants([]);
        setCheckAll(false);
        setCheckedItems({});
      } else {
        const participantsData = await trainingNeedsAPI.getTrainingNeeds(participantsParams);
        const filteredParticipants = Array.isArray(participantsData)
          ? participantsData.filter(p => p.nomin_confirm !== 'Y')
          : [];
        setParticipants(filteredParticipants);
        setCheckAll(false);
        setCheckedItems({});
      }

      setShowResults(true);
    } catch {
      alert('Failed to fetch batch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnnounce = async () => {
    setAnnounceMsg(null);
    const checkedParticipants = participants.filter((_, i) => checkedItems[i]);
    if (!checkedParticipants.length) {
      setAnnounceMsg({ success: false, text: 'No participants selected.' });
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const identifiedBy = user?.sal_code != null ? String(user.sal_code) : null;

    const startDate = batchInfo?.start_date ? new Date(batchInfo.start_date).toLocaleDateString('en-GB') : '';
    const endDate = batchInfo?.end_date ? new Date(batchInfo.end_date).toLocaleDateString('en-GB') : '';
    const startDateFormatted = startDate.split('/').map((p, i) => i === 2 ? p.slice(-2) : p).join('/');
    const endDateFormatted = endDate.split('/').map((p, i) => i === 2 ? p.slice(-2) : p).join('/');
    const message = `We are pleased to inform you that you are nominated for the training held from ${startDateFormatted}(${batchInfo?.start_time || '00.00'}) till ${endDateFormatted}(${batchInfo?.end_time || '00.00'})`;

    try {
      const updateResults = await Promise.allSettled(
        checkedParticipants.map((p) =>
          trainingNeedsAPI.update(p.sal_code, p.trng_course_code, p.date_of_entry || p.date_of_need, {
            updated_nomin_batch_sr_no: batchSrNo,
            nomin_confirm: 'Y',
            date_of_first_nomination: today,
            updated_by: identifiedBy,
            identified_by: identifiedBy
          })
        )
      );

      const failedUpdates = updateResults.filter(r => r.status === 'rejected');
      if (failedUpdates.length) {
        console.error('[Announcement] Participant updates failed:', failedUpdates);
        setAnnounceMsg({ success: false, text: `Failed to announce ${failedUpdates.length} participant(s). Please try again.` });
        return;
      }

      await batchStatusAPI.update(batchSrNo, { trng_status: 'SCHEDULED', branch: batchInfo?.branch });

      // Build trainingData payload used across all email groups
      const trainingData = {
        batchSrNo,
        funcSubAreaName,
        trngCourseName,
        startDate,
        endDate,
        startTime: batchInfo?.start_time || '',
        endTime: batchInfo?.end_time || '',
        venue: batchInfo?.venue || '',
        trngLocation: batchInfo?.trng_location || '',
        trngCity: batchInfo?.trng_city || '',
        batchSize: batchInfo?.batch_size || '',
        faculty: facultyName || ''
      };

      // Fire all email groups in parallel, errors are non-blocking
      try {
        const emailPromises = [];

        // Group 1 — Employee (L): one email per checked participant
        checkedParticipants.forEach((p) => {
          if (p.emp_email) {
            emailPromises.push(
              emailAPI.notifyEmployee({
                employeeData: { emailId: p.emp_email, name: p.emp_name || String(p.sal_code) },
                trainingData
              }).catch(() => {})
            );
          }
        });

        // Group 2 — Superior (L+1): one email per unique superior email with all their employees listed
        const uniqueSuperiors = {};
        checkedParticipants.forEach((p) => {
          if (p.sup_email) {
            if (!uniqueSuperiors[p.sup_email])
              uniqueSuperiors[p.sup_email] = { name: p.sup_name || '', empNames: [] };
            uniqueSuperiors[p.sup_email].empNames.push(p.emp_name || String(p.sal_code));
          }
        });
        Object.entries(uniqueSuperiors).forEach(([email, sup]) => {
          emailPromises.push(
            emailAPI.notifySuperior({
              superiorData: { emailId: email, name: sup.name },
              employeeNames: sup.empNames.join(', '),
              trainingData
            }).catch(() => {})
          );
        });

        // Group 3 — Regional Service Manager: unique by email
        const uniqueRSM = new Set();
        checkedParticipants.forEach((p) => {
          if (p.regional_service_manager_email) uniqueRSM.add(p.regional_service_manager_email);
        });
        uniqueRSM.forEach((email) => {
          emailPromises.push(
            emailAPI.notifyManager({
              managerData: { emailId: email },
              trainingData
            }).catch(() => {})
          );
        });

        // Group 4 — Branch Service Incharge: unique by email
        const uniqueBSI = new Set();
        checkedParticipants.forEach((p) => {
          if (p.branch_service_incharge_email) uniqueBSI.add(p.branch_service_incharge_email);
        });
        uniqueBSI.forEach((email) => {
          emailPromises.push(
            emailAPI.notifyManager({
              managerData: { emailId: email },
              trainingData
            }).catch(() => {})
          );
        });

        await Promise.all(emailPromises);
      } catch { /* skip email errors */ }

      // Group 5 — Faculty via /notify/faculty (sequential)
      const facultyCodesForEmail = [
        batchInfo?.faculty_code,
        batchInfo?.faculty_code_1,
        batchInfo?.faculty_code_2,
        batchInfo?.faculty_code_3,
        batchInfo?.faculty_code_4,
      ].filter(Boolean);
      for (const fc of facultyCodesForEmail) {
        try {
          const fd = await facultyAPI.getAll({ faculty_code: fc });
          const fac = Array.isArray(fd) ? fd[0] : null;
          if (!fac) continue;
          const facEmail = fac.faculty_email || fac.email || '';
          if (!facEmail || !facEmail.includes('@')) continue;
          await emailAPI.notifyFaculty({
            facultyData: { emailId: facEmail, name: fac.faculty_name || '' },
            trainingData
          }).catch(() => {});
        } catch { /* skip */ }
      }

      const smsMessages = checkedParticipants
        .filter((p) => p.emp_mobile && String(p.emp_mobile).trim())
        .map((p) => ({
          systemCode: 'TNS',
          divisionCode: 'GIL',
          mobileNo: String(p.emp_mobile).trim(),
          message
        }));

      if (smsMessages.length) {
        try {
          await smsAPI.sendAnnouncement(smsMessages);
        } catch { /* skip */ }
      }

      // Build summary for post-announcement page
      const smBsiList = [];
      const seenRSMCodes = new Set();
      const seenBSICodes = new Set();
      checkedParticipants.forEach((p) => {
        if (p.regional_service_manager_email && !seenRSMCodes.has(p.regional_service_manager_email)) {
          seenRSMCodes.add(p.regional_service_manager_email);
          smBsiList.push({ label: `${p.regional_service_manager_email} (Salary Code: ${p.regional_service_manager_code || ''})` });
        }
        if (p.branch_service_incharge_email && !seenBSICodes.has(p.branch_service_incharge_email)) {
          seenBSICodes.add(p.branch_service_incharge_email);
          smBsiList.push({ label: `${p.branch_service_incharge_email} (Salary Code: ${p.branch_service_incharge_code || ''})` });
        }
      });

      const superiorList = [];
      const seenSupEmails = new Set();
      checkedParticipants.forEach((p) => {
        if (p.sup_email && !seenSupEmails.has(p.sup_email)) {
          seenSupEmails.add(p.sup_email);
          superiorList.push({ email: p.sup_email, name: p.sup_name || '', code: p.sup_emp_code || '' });
        }
      });

      const facultyList = [];
      const facultyCodes = [
        batchInfo?.faculty_code, batchInfo?.faculty_code_1, batchInfo?.faculty_code_2,
        batchInfo?.faculty_code_3, batchInfo?.faculty_code_4,
      ].filter(Boolean);
      for (const fc of facultyCodes) {
        try {
          const fd = await facultyAPI.getAll({ faculty_code: fc });
          const fac = Array.isArray(fd) ? fd[0] : null;
          const fe = fac?.faculty_email || fac?.email || '';
          if (fe) facultyList.push({ email: fe, name: fac.faculty_name || '' });
        } catch { /* skip */ }
      }

      setAnnounceMsg({
        success: true,
        text: `Successfully announced ${checkedParticipants.length} participant(s) for Batch #${batchSrNo} — ${trngCourseName} (${batchInfo?.start_date ? new Date(batchInfo.start_date).toLocaleDateString('en-GB') : ''} to ${batchInfo?.end_date ? new Date(batchInfo.end_date).toLocaleDateString('en-GB') : ''}).`
      });
      setAnnounceSummary({
        employees: checkedParticipants.map(p => p.emp_name || String(p.sal_code)),
        smBsiList,
        superiorList,
        facultyList,
      });
      setParticipants([]);
      setCheckedItems({});
      setCheckAll(false);
    } catch {
      setAnnounceMsg({ success: false, text: 'Failed to announce. Please try again.' });
    }
  };

  const numDays = batchInfo?.start_date && batchInfo?.end_date
    ? Math.floor((new Date(batchInfo.end_date) - new Date(batchInfo.start_date)) / (1000 * 60 * 60 * 24)) + 1
    : "";

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh', padding: '20px' }}>
      {showBatchViewer && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '90%', maxHeight: '90%', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3>Select Batch</h3>
              <button onClick={() => setShowBatchViewer(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <BatchViewer selectable={true} onSelect={handleBatchSelect} autoLoad={true} filterStatus={["FORMED", "SCHEDULED"]} loadTrigger={showBatchViewer} />
          </div>
        </div>
      )}
      <form className="training-form">
        <div className="form-row">
          <label>Batch Sr.No.</label>
          <input type="text" value={batchSrNo} onChange={(e) => setBatchSrNo(e.target.value)} />
          <button type="button" className="get-btn" onClick={() => setShowBatchViewer(true)}>Get</button>
        </div>
        <div className="form-heading">Short list according to</div>
        <div className="form-row">
          <label>Division</label>
          <p>"Appliances" </p>
        </div>
        <div className="form-row">
          <label>Zone</label>
          <select value={zone} onChange={(e) => setZone(e.target.value)}>
            <option value="">--None--</option>
            {zones.map((z) => (<option key={z} value={z}>{z}</option>))}
          </select>
        </div>
        <div className="form-row">
          <label>Branch</label>
          <select value={branch} onChange={(e) => setBranch(e.target.value)}>
            <option value="">--None--</option>
            {branches.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
        </div>
        <div className="form-row">
          <label>From Date</label>
          <div className="ann-date-wrap" onClick={() => setFromDateOpen(true)}>
            <DatePicker
              open={fromDateOpen}
              onClickOutside={() => setFromDateOpen(false)}
              selected={fromDate}
              onChange={(date) => { setFromDate(date); setFromDateOpen(false); }}
              dateFormat="dd/MM/yyyy"
              className="ann-date-input"
              placeholderText="Select from date"
              showIcon
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              customInput={<input readOnly />}
            />
          </div>
        </div>
        <div className="form-row">
          <label>To Date</label>
          <div className="ann-date-wrap" onClick={() => setToDateOpen(true)}>
            <DatePicker
              open={toDateOpen}
              onClickOutside={() => setToDateOpen(false)}
              selected={toDate}
              onChange={(date) => { setToDate(date); setToDateOpen(false); }}
              dateFormat="dd/MM/yyyy"
              className="ann-date-input"
              placeholderText="Select to date"
              minDate={fromDate}
              showIcon
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              customInput={<input readOnly />}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', margin: '5px 0' }}>
          <button type="submit" className="show-btn show-btn-outline" onClick={handleShowShortListed} disabled={loading}>
            {loading ? 'Loading...' : 'Show Short Listed People'}
          </button>
        </div>
      </form>

      {/* Results section */}
      {showResults && (
        <div style={{ marginTop: '30px', width: '100%', maxWidth: '1000px' }}>
          {/* Batch info table */}
          <h3 style={{ marginBottom: '15px', fontFamily: 'GEG-Body, sans-serif', textAlign: 'center' }}>Batch Details</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontFamily: 'GEG-Body, sans-serif', fontSize: '12px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Functional Area</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{funcSubAreaName}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Training Course Name</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{trngCourseName}[{batchInfo?.trng_course_code}]</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Start Date</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{batchInfo?.start_date ? new Date(batchInfo.start_date).toLocaleDateString('en-GB') : ''}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>End Date</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{batchInfo?.end_date ? new Date(batchInfo.end_date).toLocaleDateString('en-GB') : ''}</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Start Time</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{batchInfo?.start_time}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>End Time</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{batchInfo?.end_time}</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>No. of Days</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{numDays}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Faculty</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{facultyName}</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Batch Size</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{batchInfo?.batch_size}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Venue,Location,City</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{batchInfo?.venue},{batchInfo?.trng_location},{batchInfo?.trng_city}</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Total Faculty Cost</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>{batchInfo?.total_faculty_cost}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}></td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}></td>
              </tr>
            </tbody>
          </table>

          {announceSummary ? (
            <div style={{ fontFamily: 'GEG-Body, sans-serif', fontSize: '13px', maxWidth: '600px', margin: '0 auto', lineHeight: '1.8' }}>
              {announceMsg && (
                <div style={{
                  padding: '8px 16px', borderRadius: '4px', marginBottom: '16px',
                  backgroundColor: announceMsg.success ? '#e6f4ea' : '#fdecea',
                  border: `1px solid ${announceMsg.success ? '#34a853' : '#d93025'}`,
                  color: announceMsg.success ? '#1e7e34' : '#c0392b',
                  fontSize: '12.5px', textAlign: 'center'
                }}>
                  {announceMsg.text}
                </div>
              )}

              <div style={{ marginBottom: '12px' }}>
                {announceSummary.employees.map((name, i) => (
                  <div key={i} style={{ fontWeight: 'bold', textTransform: 'uppercase', color: 'red' }}>{name}</div>
                ))}
              </div>

              {announceSummary.smBsiList.length > 0 && (
                <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '4px', color: 'blue', fontWeight: 'bold' }}>Mail has been sent to following Service Managers and Branch Service Incharge</div>
                  {announceSummary.smBsiList.map((m, i) => (
                    <div key={i} style={{ color: 'blue' }}>{m.label}</div>
                  ))}
                </div>
              )}

              {announceSummary.superiorList.length > 0 && (
                <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '4px', color: 'blue', fontWeight: 'bold' }}>Mail has been sent to following L+1</div>
                  {announceSummary.superiorList.map((s, i) => (
                    <div key={i} style={{ color: 'blue' }}>{s.email}{s.code ? ` (Sal Code- ${s.code})` : ''}</div>
                  ))}
                </div>
              )}

              {announceSummary.facultyList.length > 0 && (
                <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '4px', color: 'blue', fontWeight: 'bold' }}>Mail has been sent to following Faculty</div>
                  {announceSummary.facultyList.map((f, i) => (
                    <div key={i} style={{ color: 'blue' }}>{f.email}{f.name ? ` (Faculty Name- ${f.name})` : ''}</div>
                  ))}
                </div>
              )}
            </div>
          ) : isFreelanceBatch ? (
            <p style={{ color: 'green', fontFamily: 'GEG-Body, sans-serif', fontSize: '13px', textAlign: 'center', margin: '16px 0' }}>
              This is a Freelance batch it is announced already
            </p>
          ) : batchError ? (
            <p style={{ color: 'red', fontFamily: 'GEG-Body, sans-serif', fontSize: '13px', textAlign: 'center', margin: '16px 0' }}>
              {batchError}
            </p>
          ) : (
            <>
              {/* Participants table */}
              <h3 style={{ marginBottom: '15px', fontFamily: 'GEG-Body, sans-serif', textAlign: 'center' }}>Shortlisted People</h3>
              <table style={{ border: '1px solid #000', borderCollapse: 'collapse', width: '100%', fontFamily: 'GEG-Body, sans-serif', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ backgroundColor: '#777', color: '#fff', textAlign: 'center', padding: '8px', border: '1px solid #000' }}>Sr.No.</th>
                    <th style={{ backgroundColor: '#777', color: '#fff', textAlign: 'center', padding: '8px', border: '1px solid #000' }}>Employee's Salary Code<br /><small>Email<br />Name</small></th>
                    <th style={{ backgroundColor: '#777', color: '#fff', textAlign: 'center', padding: '8px', border: '1px solid #000' }}>BC/RC/CC<br /><small>Division</small></th>
                    <th style={{ backgroundColor: '#777', color: '#fff', textAlign: 'center', padding: '8px', border: '1px solid #000' }}>Superior's Salary Code<br /><small>Email<br />Name</small></th>
                    <th style={{ backgroundColor: '#777', color: '#fff', textAlign: 'center', padding: '8px', border: '1px solid #000' }}>
                      Identified<br />
                      <input type="checkbox" checked={checkAll} onChange={handleCheckAll} style={{ margin: '0 5px' }} /> Check All
                    </th>
                    <th style={{ backgroundColor: '#777', color: '#fff', textAlign: 'center', padding: '8px', border: '1px solid #000' }}>Defaulter?</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.length === 0 ? (
                    <tr><td colSpan="6" style={{ padding: '6px', border: '1px solid #000', textAlign: 'center', fontStyle: 'italic' }}>No participants found</td></tr>
                  ) : (
                    participants.map((p, i) => (
                      <tr key={p.sal_code || i}>
                        <td style={{ padding: '6px', border: '1px solid #000', textAlign: 'center' }}>{i + 1}</td>
                        <td style={{ padding: '6px', border: '1px solid #000' }}>{p.sal_code}<br />{p.emp_name || ''}</td>
                        <td style={{ padding: '6px', border: '1px solid #000' }}>{[p.emp_bc, p.emp_rc, p.emp_cc].filter(Boolean).join('/') || ''}<br /><small>{p.division || ''}</small></td>
                        <td style={{ padding: '6px', border: '1px solid #000' }}>{p.sup_emp_code || ''}<br />{p.sup_name || ''}</td>
                        <td style={{ padding: '6px', border: '1px solid #000', textAlign: 'center' }}>
                          <input type="checkbox" checked={checkedItems[i] || false} onChange={() => handleItemCheck(i)} />
                        </td>
                        <td style={{ padding: '6px', border: '1px solid #000' }}></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {announceMsg && (
                <div style={{
                  padding: '8px 16px', borderRadius: '4px', margin: '12px auto',
                  backgroundColor: announceMsg.success ? '#e6f4ea' : '#fdecea',
                  border: `1px solid ${announceMsg.success ? '#34a853' : '#d93025'}`,
                  color: announceMsg.success ? '#1e7e34' : '#c0392b',
                  fontFamily: 'GEG-Body, sans-serif', fontSize: '12.5px', textAlign: 'center', maxWidth: '600px'
                }}>
                  {announceMsg.text}
                  <button onClick={() => setAnnounceMsg(null)} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: 'inherit' }}>×</button>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                <button className="show-btn" onClick={handleAnnounce}>Announce</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Announcement;
